'use strict'
const axios = require('axios')
const abstractLogger = require('abstract-logging') // Abstract implementation, is replaced by user specified instance
const isNode = require('./isNode.js')
const getXPclnHeaders = require('./get-x-pcln-headers.js')

const axiosConfig = {
  timeout: 60000,
}

const deprecationNotices = {}

// TLS certificate verification is off by default, as it always has been, for every destination
// (DX-2910). It stays off until InfoSec records accept-or-remediate and the hosts that present
// untrusted certificates are known: flipping it blind would break any internal host with a
// self-signed certificate. An app opts in with PCLN_REQUEST_TLS_VERIFY=true (internal CA via
// NODE_EXTRA_CA_CERTS) to prove its destinations verify, which is how that inventory gets built.
// Read inside the isNode guard: client bundles may not define `process`.
// istanbul ignore else
if (isNode) {
  const verifyTls = String(process.env.PCLN_REQUEST_TLS_VERIFY || '').toLowerCase() === 'true'
  const https = require('https')
  const http = require('http')
  axiosConfig.httpsAgent = new https.Agent({
    rejectUnauthorized: verifyTls,
    keepAlive: true,
  })
  axiosConfig.httpAgent = new http.Agent({
    keepAlive: true,
  })
}

const axiosClient = axios.create(axiosConfig)

// axios 0.x implements `timeout` with ClientRequest.setTimeout, an idle-socket timer: it fires
// only after the socket has been silent for that long, so an upstream that keeps trickling
// bytes is never cut off (measured on 12 Oct 2026: one byte every 300 ms for 3 s completed
// against `timeout: 500` in 3,022 ms). When a caller asks for a timeout, also arm a deadline on
// the whole request through an axios cancel token. A cancel token rather than AbortSignal
// because it works on every Node this package supports and in the jest 26 sandbox, which has
// no AbortController. The 60,000 ms default stays idle-only on purpose: turning it into a
// hard deadline would change behaviour for callers that stream large bodies and never asked
// for a bound (DX-3595).
const isUsableTimeout = (value) => typeof value === 'number' && Number.isFinite(value) && value > 0

function armDeadline(options) {
  if (!isUsableTimeout(options.timeout) || options.cancelToken || options.signal) return null
  const source = axios.CancelToken.source()
  options.cancelToken = source.token
  const deadline = { fired: false, message: `deadline of ${options.timeout}ms exceeded`, timer: null }
  deadline.timer = setTimeout(() => {
    deadline.fired = true
    source.cancel(deadline.message)
  }, options.timeout)
  // istanbul ignore else
  if (typeof deadline.timer.unref === 'function') deadline.timer.unref()
  return deadline
}

// axios reports the cancellation with the message we passed. Callers log err.message and some
// branch on it, so a deadline that fired is reported the way an idle timeout is, with the
// budget in the message and the same ECONNABORTED code, and marked so the two can be told apart.
// Identifier-shaped path segments: 16+ hex characters (cguid, request ids), long digit runs
// (offer and itinerary numbers), UUIDs. Masked to `:id` so the route stays groupable without
// carrying the identifier (DX-3369).
// Three small patterns rather than one alternation: SonarQube S5843 caps regex complexity at 20.
const HEX_ID = /^[0-9a-f]{16,}$/i
const NUMERIC_ID = /^\d{6,}$/
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const isIdentifierSegment = (segment) =>
  HEX_ID.test(segment) || NUMERIC_ID.test(segment) || UUID.test(segment)

const maskRoute = (pathname) =>
  pathname
    .split('/')
    .map((segment) => (isIdentifierSegment(segment) ? ':id' : segment))
    .join('/')

function describeUrl(url) {
  if (typeof url !== 'string' || url === '') return {}
  try {
    const parsed = new URL(url)
    return { origin: parsed.origin, route: maskRoute(parsed.pathname) }
  } catch {
    // Relative or malformed: no host to report, but the same masking. `legacyResolveServiceUrl`
    // builds `/pws/v0/...` whenever the host is not configured (config.js defaults baseUrl to
    // '/'), so this branch carries the SETI rollcall's cguid segment too (review on #22548).
    return { route: maskRoute(url.split('?')[0]) }
  }
}

function asDeadlineError(err, deadline) {
  if (!deadline) return err
  clearTimeout(deadline.timer)
  if (!deadline.fired || !err || err.message !== deadline.message) return err
  const error = new Error(deadline.message)
  error.code = 'ECONNABORTED'
  error.isDeadline = true
  error.config = err.config
  error.request = err.request
  return error
}

function cleanSlashes(string) {
  return string.replace(/(^\/)|(\/$)/g, '')
}

function legacyResolveServiceUrl(service, endpoint, logger) {
  let uri = service.uri
  const _f5Type = service.f5Type

  const legacyNodeConfig = require('./config/config')
  // service-specific request settings
  let base = service.secure ? legacyNodeConfig.baseUrlSecure : legacyNodeConfig.baseUrl
  if (base instanceof Object) {
    base = base[_f5Type]
  }

  /* istanbul ignore next */
  if (!base || base === '/') {
    logger.warn('URL domain not defined.  Check env vars BOOTSTRAP_ENV and BOOTSTRAP_DC or @pcln/config')
  }

  // Replace starting and ending slashes to avoid repeats
  uri = cleanSlashes(base) + '/' + cleanSlashes(uri)
  // strip out non-alphanumeric/'_'/'-' characters from API endpoint
  // istanbul ignore else
  if (endpoint) {
    uri = uri + '/' + cleanSlashes(endpoint)
  }
  return uri
}

function pclnRequest(config = {}, incomingReq = {}) {
  // Merge user config with defaults
  const options = {
    discovery: true,
    logRequestBody: null,
    serviceName: '',
    endpoint: '',
    url: '',
    headers: {},
    ...config,
  }

  // Get all x-pcln- headers from incoming request to pass them forward.
  // If incomingReq.headers contains x-pcln- headers that are also being explicitly set in the options.headers,
  // defer to the values in options.headers. That's why we put the spreads in the order below.
  const xPclnHeaders = getXPclnHeaders(incomingReq)
  if (Object.getOwnPropertyNames(xPclnHeaders).length > 0) {
    options.headers = {
      ...xPclnHeaders,
      ...options.headers,
    }
  }

  const { logger = abstractLogger } = config

  if (!options.url && !deprecationNotices[options.serviceName]) {
    logger.warn(
      `Deprecation warning: the use of 'serviceName' and 'endpoint' in @pcln/request is deprecated and will be removed in a future release.  Please specify a 'url' to connect to`,
    )
    deprecationNotices[options.serviceName] = true
  }

  const baseUrl = options.serviceName ? process.env[`SERVICE_${options.serviceName.toUpperCase()}_URL`] : null

  if (baseUrl) {
    options.url = `${cleanSlashes(baseUrl)}/${cleanSlashes(options.endpoint)}`
  } else if (!options.url) {
    // If url is not passed in, try to do url resolution
    if (options.discovery) {
      // When using service discovery, endPoint must include the full route e.g. pws/v0/driveql/
      // Does not support unsecured (http) services
      // Does not have hard-coded timeouts per service
      const serviceDiscovery = require('@pcln/service-disco')
      const hostname = serviceDiscovery(options.serviceName)
      options.url = `https://${cleanSlashes(hostname)}/${cleanSlashes(options.endpoint)}`
    } else {
      // When using legacy url resolution, the basename is prefixed to the endPoint optiom
      // So i.e. do not include "/pws/v0/servicename" in the endPoint option
      const legacyNodeConfig = require('./config/config')
      const service = legacyNodeConfig.services[options.serviceName]
      if (service) {
        options.url = legacyResolveServiceUrl(service, options.endpoint, logger)
        // istanbul ignore else
        if (!options.timeout) {
          options.timeout = service.timeout
        }
      } else {
        const message = `Service not found in node config (legacy url resolution): ${options.serviceName}`
        logger.error({ error: message, serviceName: options.serviceName }, 'pcln request finished')
        return Promise.reject(new Error(message))
      }
    }
  }

  // What the 'pcln request finished' line carries. `url` and `endpoint` used to go in whole;
  // callers put identifiers in paths (the SETI rollcall carries the cguid as a path segment)
  // and query strings, so every consumer logged them at info on every call (DX-3369). The
  // line keeps what a dashboard groups on: the service, the origin and the route shape with
  // identifier-like segments masked.
  const logData = {
    serviceName: options.serviceName,
    ...describeUrl(options.url),
    timeout: options.timeout || axiosConfig.timeout,
  }
  // Only allow logging request body as development debug feature
  // Do not allow this in production environment
  if (isNode && process.env.NODE_ENV === 'development' && options.logRequestBody === true && options.data) {
    logData.body = options.data
  }

  const deadline = armDeadline(options)
  const startTime = Date.now()

  return new Promise((resolve, reject) => {
    axiosClient
      .request(options)
      .then((response) => {
        if (deadline) clearTimeout(deadline.timer)
        const elapsedTime = Date.now() - startTime
        const statusCode = response && response.status
        logger.info({ elapsedTime, statusCode, ...logData }, 'pcln request finished')
        resolve(response)
      })
      .catch((rawErr) => {
        const err = asDeadlineError(rawErr, deadline)
        const response = err.response
        const elapsedTime = Date.now() - startTime
        const statusCode = response && response.status
        logger.error({ elapsedTime, statusCode, error: err.message, ...logData }, 'pcln request finished')
        reject(err)
      })
  })
}

module.exports = pclnRequest
module.exports.describeUrl = describeUrl
